import moment, { Moment } from 'moment';
import classnames from 'classnames';

import { useEffect, useRef, useState } from 'react';

import styles from './index.module.css';

interface Props {
  time: Moment | null;
  onChange: (time: Moment) => void;
  label?: string;
}

enum DayPeriod {
  Am = 'AM',
  Pm = 'PM',
}

const TimeInput: React.FC<Props> = ({ time, onChange, label }) => {
  const hourInputRef = useRef<HTMLInputElement>(null);
  const minuteInputRef = useRef<HTMLInputElement>(null);
  const [hours, setHours] = useState<string>('');
  const [minutes, setMinutes] = useState<string>('');
  const [dayPeriod, setDayPeriod] = useState<DayPeriod | null>(null);

  const defaultTime = moment().hour(0).minute(0);

  useEffect(() => {
    if (!time) {
      setHours('');
      setMinutes('');
      setDayPeriod(null);
      return;
    }
    const parsedTime = moment(time.toDate()).locale('en');
    setHours(parsedTime.format('hh'));
    setMinutes(parsedTime.format('mm'));
    setDayPeriod(parsedTime.format('A') === 'AM' ? DayPeriod.Am : DayPeriod.Pm);
  }, [time]);

  const getHoursValue = (value: string, prevValue: string): string => {
    if (value.length < prevValue.length) {
      return value;
    }
    if (value[0] > '2') {
      return '0' + value[0];
    }
    if (value.length === 2) {
      const int = parseInt(value, 10);
      if (int < 1) return '01';
      if (int > 12) {
        setDayPeriod(DayPeriod.Pm);
        const pmHour = int - 12;
        return getHoursValue(
          `${pmHour >= 10 ? pmHour : `0${pmHour}`}`,
          prevValue,
        );
      }
    }
    return value.slice(0, 2);
  };

  const getMinutesValue = (value: string, prevValue: string): string => {
    if (value.length < prevValue.length) {
      return value;
    }
    if (value[0] > '6') {
      return '0' + value[0];
    }
    if (value.length === 2) {
      const int = parseInt(value, 10);
      if (int < 0) return '01';
      if (int > 60) return '60';
    }
    return value.slice(0, 2);
  };

  const handleKeyDown = (args: {
    event: React.KeyboardEvent<HTMLInputElement>;
    previousFocus?: React.RefObject<HTMLInputElement>;
    nextFocus?: React.RefObject<HTMLInputElement>;
  }) => {
    const { event, previousFocus, nextFocus } = args;
    const { key } = event;
    const valid = /^\d+$/.test(key);
    if (valid && nextFocus && event.currentTarget.value.length === 2) {
      nextFocus.current?.focus();
    } else if (
      previousFocus &&
      key === 'Backspace' &&
      !event.currentTarget.value
    ) {
      previousFocus.current?.focus();
    }
  };

  const handleChange = (event: React.FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget;
    if (value.length > 2 || (value && !/^\d+$/.test(value))) {
      return;
    }
    if (name === 'hour') {
      const hoursValue = getHoursValue(value, hours);
      const isPm = value.length === 2 && parseInt(value, 10) > 12;
      setHours(hoursValue);
      if (hoursValue.length === 2) {
        onChange((time || defaultTime).hour(+hoursValue + (isPm ? 12 : 0)));
      }
    } else if (name === 'minute') {
      const minuteValue = getMinutesValue(value, minutes);
      setMinutes(minuteValue);
      if (minuteValue.length === 2) {
        onChange((time || defaultTime).minute(+minuteValue));
      }
    }
  };

  return (
    <div className={styles.TimeInput}>
      {label && <div className={styles.TimeLabel}>{label}</div>}
      <input
        name="hour"
        type="number"
        className={styles.TimeDigitInput}
        pattern="[0-9]*"
        ref={hourInputRef}
        value={hours}
        placeholder="--"
        onChange={handleChange}
        onKeyDown={(event) =>
          handleKeyDown({
            event,
            nextFocus: minuteInputRef,
          })
        }
      />
      <span className={styles.TimeSeparator}>:</span>
      <input
        name="minute"
        type="number"
        className={styles.TimeDigitInput}
        pattern="[0-9]*"
        ref={minuteInputRef}
        value={minutes}
        placeholder="--"
        onChange={handleChange}
        onKeyDown={(event) =>
          handleKeyDown({
            event,
            previousFocus: hourInputRef,
          })
        }
      />
      <div className={styles.AmPmWrapper}>
        <span
          className={classnames({
            [styles.AmPmButton]: true,
            [styles.Selected]: dayPeriod === DayPeriod.Am,
          })}
          onClick={() => {
            if (dayPeriod === DayPeriod.Am) return;
            setDayPeriod(DayPeriod.Am);
            time && onChange(time.subtract(12, 'hours'));
          }}
        >
          AM
        </span>
        <span
          className={classnames({
            [styles.AmPmButton]: true,
            [styles.Selected]: dayPeriod === DayPeriod.Pm,
          })}
          onClick={() => {
            if (dayPeriod === DayPeriod.Pm) return;
            setDayPeriod(DayPeriod.Pm);
            time && onChange(time.add(12, 'hours'));
          }}
        >
          PM
        </span>
      </div>
    </div>
  );
};

export default TimeInput;
